import PDFViewer from "./PDFViewer";
import { useEffect, useRef } from "react";
import logo from "../../assets/fav.webp"

// --- Universal Video Renderer ---
const UniversalVideoPlayer = ({ url }) => {
  const videoRef = useRef(null);

  // Detect YouTube
  const getYouTubeEmbedUrl = (url) => {
    const match = url.match(
      /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/
    );
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  // Detect Google Drive
  const getDrivePreviewUrl = (url) => {
    const match = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
    return match ? `https://drive.google.com/file/d/${match[1]}/preview` : null;
  };

  // Detect Vimeo
  const getVimeoEmbedUrl = (url) => {
    // Support various Vimeo URL formats:
    // https://vimeo.com/123456789
    // https://player.vimeo.com/video/123456789
    // https://vimeo.com/channels/staffpicks/123456789
    // https://vimeo.com/ondemand/example/123456789
    const match = url.match(/(?:vimeo\.com\/(?:channels\/[^\/]+\/|ondemand\/[^\/]+\/)?|player\.vimeo\.com\/video\/)(\d+)/);
    return match ? `https://player.vimeo.com/video/${match[1]}` : null;
  };

  // Detect Vimeo embed code
  const getVimeoEmbedCode = (url) => {
    // Check if the URL contains Vimeo embed code HTML
    const embedMatch = url.match(/<div[^>]*><iframe[^>]*src="https:\/\/player\.vimeo\.com\/video\/(\d+)[^"]*"[^>]*><\/iframe><\/div>/);
    if (embedMatch) {
      return {
        videoId: embedMatch[1],
        fullEmbedCode: url
      };
    }
    return null;
  };

  // Detect VdoCipher embed code
  const getVdoCipherEmbedCode = (url) => {
    // Check if the URL contains VdoCipher embed code HTML (both v1 and v2 formats)
    const v1Match = url.match(/<div[^>]*><iframe[^>]*src="https:\/\/player\.vdocipher\.com\/player\.php\?[^"]*"[^>]*><\/iframe><\/div>/);
    const v2Match = url.match(/<iframe[^>]*src="https:\/\/player\.vdocipher\.com\/v2\/\?[^"]*"[^>]*><\/iframe>/);
    
    if (v1Match || v2Match) {
      console.log('VdoCipher embed code detected:', url);
      return {
        fullEmbedCode: url
      };
    }
    return null;
  };

  // Detect direct video (mp4/webm/etc)
  const isDirectVideo = (url) => {
    return url.match(/\.(mp4|webm|ogg)(\?|$)/i);
  };

  useEffect(() => {
    // Disable right-click for direct video
    const videoEl = videoRef.current;
    if (videoEl) {
      const handleContextMenu = (e) => e.preventDefault();
      videoEl.addEventListener("contextmenu", handleContextMenu);
      return () => videoEl.removeEventListener("contextmenu", handleContextMenu);
    }
  }, [url]);

  // Render logic
  if (!url) {
    return (
      <div className="video-placeholder d-flex align-items-center justify-content-center bg-light" style={{ height: '400px' }}>
        <div className="text-center">
          <i className="fas fa-video fa-3x text-muted mb-3"></i>
          <p className="text-muted">Video content will be available here</p>
        </div>
      </div>
    );
  }

  // YouTube
  const yt = getYouTubeEmbedUrl(url);
  if (yt) {
    return (
      <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
        <iframe
          src={yt}
          title="YouTube Video"
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
        />
      </div>
    );
  }

  // Google Drive
  const drive = getDrivePreviewUrl(url);
  if (drive) {
    return (
      <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
        <iframe
          src={drive}
          allow="autoplay"
          frameBorder="0"
          allowFullScreen
          title="Google Drive Video"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
        ></iframe>
        {/* Mask the pop-out icon */}
        <div
          style={{
            width: 48,
            height: 48,
            position: "absolute",
            right: 6,
            top: 6,
            zIndex: 2,
            pointerEvents: "auto",
          }}
        >
          {/* Transparent PNG (invisible, just blocks clicks) */}
          <img
            src={logo}
            alt="NAI"
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      </div>
    );
  }

  // Vimeo Embed Code (for private videos)
  const vimeoEmbed = getVimeoEmbedCode(url);
  if (vimeoEmbed) {
    return (
      <div 
        dangerouslySetInnerHTML={{ __html: vimeoEmbed.fullEmbedCode }}
        style={{ position: "relative", width: "100%" }}
      />
    );
  }

  // VdoCipher Embed Code
  const vdoCipherEmbed = getVdoCipherEmbedCode(url);
  if (vdoCipherEmbed) {
    return (
      <div 
        dangerouslySetInnerHTML={{ __html: vdoCipherEmbed.fullEmbedCode }}
        style={{ position: "relative", width: "100%" }}
      />
    );
  }

  // Vimeo URL
  const vimeo = getVimeoEmbedUrl(url);
  if (vimeo) {
    return (
      <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
        <iframe
          src={vimeo}
          title="Vimeo Video"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
        />
      </div>
    );
  }


  // Direct video (mp4/webm)
  if (isDirectVideo(url)) {
    return (
      <video
        ref={videoRef}
        controls
        className="w-100"
        style={{ maxHeight: '500px', background: '#222' }}
        controlsList="nodownload nofullscreen noremoteplayback"
        disablePictureInPicture
      >
        <source src={url} />
        Your browser does not support the video tag.
      </video>
    );
  }

  // Unknown or unsupported
  return (
    <div className="video-placeholder d-flex align-items-center justify-content-center bg-light" style={{ height: '400px' }}>
      <div className="text-center">
        <i className="fas fa-video fa-3x text-muted mb-3"></i>
        <p className="text-muted">Video source not supported</p>
      </div>
    </div>
  );
};

// --- Your LessonViewer ---
const LessonViewer = ({ lesson, activeTab, onTabChange, renderTabContent }) => {
  if (!lesson) {
    return (
      <div className="lesson-viewer">
        <div className="d-flex align-items-center justify-content-center h-100">
          <div className="text-center">
            <i className="fas fa-play-circle fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">Select a lesson to start learning</h5>
          </div>
        </div>
      </div>
    );
  }

  const renderLessonContent = () => {


    // Detect by MIME type
    const isImageMime = (mime) =>
      !!mime && mime.trim().toLowerCase().startsWith("image/");

    // Detect by file extension (fallback)
    const isImageFile = (url) =>
      /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(url);

    // Detect PDF by MIME type
    const isPdfMime = (mime) =>
      !!mime && mime.trim().toLowerCase() === "application/pdf";


    switch (lesson.content_type) {
      case 'video':
        return (
          <div className="video-container">
            <UniversalVideoPlayer url={lesson.video} />
          </div>
        );
        case 'doc':
          return (
            <div className="document-container">
              {lesson.document ? (
                <div className="document-viewer">
                  <div className="alert alert-info">
                    <i className="fas fa-file-alt me-2"></i>
                    {/* Try by MIME, fallback to extension */}
                    {isImageMime(lesson.document_mime) || isImageFile(lesson.document) ? (
                      <img
                        src={lesson.document}
                        alt="Lesson Document"
                        style={{
                          maxWidth: "100%",
                          maxHeight: 500,
                          display: "block",
                          margin: "0 auto",
                          borderRadius: 16,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.07)"
                        }}
                      />
                    ) : isPdfMime(lesson.document_mime) || /\.pdf$/i.test(lesson.document) ? (
                      <div onContextMenu={e => e.preventDefault()}>
                        <PDFViewer pdfUrl={lesson.document} />
                      </div>
                    ) : (
                      <span>Unsupported file type.</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="document-placeholder d-flex align-items-center justify-content-center bg-light" style={{ height: '400px' }}>
                  <div className="text-center">
                    <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                    <p className="text-muted">Document content will be available here</p>
                  </div>
                </div>
              )}
            </div>
          );
        
        case 'link':
        return (
          <div className="link-container text-center">
            <div className="alert alert-info">
              <i className="fas fa-link me-2"></i>
              <p>Click below to view it.</p>
              <a
                href={lesson.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <i className="fas fa-external-link-alt me-1"></i> View Resource
              </a>
            </div>
          </div>
        );
      default:
        return (
          <div className="content-placeholder d-flex align-items-center justify-content-center bg-light" style={{ height: '400px' }}>
            <div className="text-center">
              <i className="fas fa-file fa-3x text-muted mb-3"></i>
              <p className="text-muted">Lesson content will be displayed here</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="lesson-viewer">
      <div className="lesson-header mb-4 mx-2 mt-2">
        <h4>{lesson.title}</h4>
        <span className="badge bg-secondary">{lesson.content_type.toUpperCase()}</span>
      </div>

      {renderLessonContent()}

      <div className="lesson-tabs mt-4">
        <button
          className={`lesson-tab ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => onTabChange("overview")}
        >
          <i className="fas fa-list me-2"></i>
          Overview
        </button>
        <button
          className={`lesson-tab ${activeTab === "exercise-files" ? "active" : ""}`}
          onClick={() => onTabChange("exercise-files")}
        >
          <i className="fas fa-file-download me-2"></i>
          Exercise Files
        </button>
      </div>

      <div className="lesson-content">{renderTabContent()}</div>
    </div>
  );
};

export default LessonViewer;
